import { useState, useMemo, useCallback } from 'react';
import {
  welcomeTopicConfigs,
  welcomeSteps,
  welcomeVideos,
  welcomeGuides,
  welcomeStats,
  welcomeRules,
  getStepsByTopic,
  getTotalOnboardingSeconds,
  type WelcomeTopic,
  type WelcomeFormat,
} from '@/mocks/rfwWelcome';

type SidebarView = 'recorrido' | 'temas' | 'videos' | 'guias' | 'reglas';

export default function BienvenidaPage() {
  const [view, setView] = useState<SidebarView>('recorrido');
  const [activeTopic, setActiveTopic] = useState<WelcomeTopic>('que-es-rfw');
  const [activeFormat, setActiveFormat] = useState<WelcomeFormat | 'todas'>('todas');
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const totalSeconds = getTotalOnboardingSeconds();
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const topicSteps = useMemo(() => getStepsByTopic(activeTopic), [activeTopic]);
  const filteredSteps = useMemo(() => {
    let steps = welcomeSteps;
    if (activeFormat !== 'todas') {
      steps = steps.filter((s) => s.format === activeFormat);
    }
    return steps;
  }, [activeFormat]);

  const topicProgress = useMemo(() => {
    return welcomeTopicConfigs.map((cfg) => {
      const steps = getStepsByTopic(cfg.key);
      const completed = steps.filter((s) => completedSteps.has(s.id)).length;
      return { ...cfg, total: steps.length, completed, pct: steps.length > 0 ? Math.round((completed / steps.length) * 100) : 0 };
    });
  }, [completedSteps]);

  const overallProgress = useMemo(() => {
    const total = welcomeSteps.length;
    const done = welcomeSteps.filter((s) => completedSteps.has(s.id)).length;
    return { total, done, pct: Math.round((done / total) * 100) };
  }, [completedSteps]);

  const handleCompleteStep = useCallback((stepId: string) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }, []);

  const handleNextTopic = useCallback(() => {
    const currentIdx = welcomeTopicConfigs.findIndex((t) => t.key === activeTopic);
    if (currentIdx < welcomeTopicConfigs.length - 1) {
      setActiveTopic(welcomeTopicConfigs[currentIdx + 1].key);
      setCurrentStepIndex(0);
    }
  }, [activeTopic]);

  const handlePrevTopic = useCallback(() => {
    const currentIdx = welcomeTopicConfigs.findIndex((t) => t.key === activeTopic);
    if (currentIdx > 0) {
      setActiveTopic(welcomeTopicConfigs[currentIdx - 1].key);
      setCurrentStepIndex(0);
    }
  }, [activeTopic]);

  const currentTopicCfg = welcomeTopicConfigs.find((t) => t.key === activeTopic);
  const currentStep = topicSteps[currentStepIndex] ?? null;
  const isLastStepInTopic = currentStepIndex >= topicSteps.length - 1;
  const isLastTopic = welcomeTopicConfigs[welcomeTopicConfigs.length - 1].key === activeTopic;
  const isLastStepOverall = isLastStepInTopic && isLastTopic;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/15"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full -translate-y-1/3 translate-x-1/4 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4 blur-3xl"></div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-white/70 text-xs mb-4">
            <a href="/" className="hover:text-white transition-colors">RFW</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Sistema de Bienvenida</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <i className="ri-user-add-line text-2xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-4xl font-bold text-white">Sistema de Bienvenida</h1>
                  <p className="text-white/70 text-sm">Retener nuevos usuarios</p>
                </div>
              </div>
              <p className="text-white/80 text-sm md:text-base max-w-lg">
                Bienvenido a RFW. En menos de 2 minutos vas a entender todo lo que necesitás para empezar a disfrutar la plataforma. Elegí tu formato: pasos rápidos, videos cortos o guías visuales.
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{welcomeStats.totalNewUsersToday.toLocaleString()}</div>
                <div className="text-xs text-white/70">Nuevos Hoy</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{welcomeStats.completionRate}%</div>
                <div className="text-xs text-white/70">Completan</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">{minutes}:{String(seconds).padStart(2, '0')}</div>
                <div className="text-xs text-white/70">Duración</div>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-8">
            <div className="flex items-center justify-between text-xs text-white/70 mb-2">
              <span>Progreso general</span>
              <span>{overallProgress.done}/{overallProgress.total} pasos · {overallProgress.pct}%</span>
            </div>
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${overallProgress.pct}%` }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Pulse Bar */}
      <section className="bg-background-100 border-b border-background-200/70">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex flex-wrap items-center gap-4 md:gap-6 overflow-x-auto">
          <div className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-foreground-700">BIENVENIDA</span>
          </div>
          {[
            { icon: 'ri-user-add-line', color: 'text-emerald-600', value: welcomeStats.totalNewUsersToday.toLocaleString(), label: 'Nuevos Hoy' },
            { icon: 'ri-check-double-line', color: 'text-amber-600', value: `${welcomeStats.completionRate}%`, label: 'Completan' },
            { icon: 'ri-timer-line', color: 'text-rose-600', value: `${welcomeStats.averageCompletionSeconds}s`, label: 'Promedio' },
            { icon: 'ri-eye-line', color: 'text-violet-600', value: 'Puntos', label: 'Más Visto', long: true },
            { icon: 'ri-team-line', color: 'text-sky-600', value: 'Clubes', label: 'Más Saltado', long: true },
          ].map((m, i) => (
            <div key={i} className={`flex items-center gap-1.5 whitespace-nowrap text-xs ${m.long ? 'hidden md:flex' : ''}`}>
              <i className={`${m.icon} ${m.color}`}></i>
              <span className="font-semibold text-foreground-950">{m.value}</span>
              <span className="text-foreground-500">{m.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="w-full lg:w-56 flex-shrink-0">
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {([
                { key: 'recorrido', label: 'Recorrido', icon: 'ri-footprint-line', color: 'text-emerald-600' },
                { key: 'temas', label: 'Temas', icon: 'ri-stack-line', color: 'text-amber-600' },
                { key: 'videos', label: 'Videos', icon: 'ri-play-circle-line', color: 'text-rose-600' },
                { key: 'guias', label: 'Guías', icon: 'ri-book-open-line', color: 'text-sky-600' },
                { key: 'reglas', label: 'Reglas', icon: 'ri-scales-line', color: 'text-violet-600' },
              ] as { key: SidebarView; label: string; icon: string; color: string }[]).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setView(item.key)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    view === item.key
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${item.icon} ${view === item.key ? 'text-emerald-600' : ''}`}></i>
                  <span className="flex-1">{item.label}</span>
                </button>
              ))}
            </nav>

            {/* Quick topic nav */}
            <div className="hidden lg:block mt-5 pt-5 border-t border-background-200/70">
              <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wide">Temas</div>
              <div className="space-y-1">
                {welcomeTopicConfigs.map((cfg) => {
                  const progress = topicProgress.find((p) => p.key === cfg.key);
                  const isCurrent = activeTopic === cfg.key;
                  return (
                    <button
                      key={cfg.key}
                      onClick={() => { setActiveTopic(cfg.key); setView('recorrido'); setCurrentStepIndex(0); }}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-colors cursor-pointer ${
                        isCurrent ? `${cfg.bgColor} ${cfg.color}` : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={cfg.icon}></i>
                      <span className="flex-1 text-left">{cfg.title}</span>
                      {progress && (
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          progress.pct === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-background-100 text-foreground-400'
                        }`}>
                          {progress.completed}/{progress.total}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Topic progress */}
            <div className="hidden lg:block mt-5 pt-5 border-t border-background-200/70">
              <div className="text-xs font-semibold text-foreground-500 mb-2 uppercase tracking-wide">Tu progreso</div>
              {topicProgress.map((tp) => (
                <div key={tp.key} className="mb-2">
                  <div className="flex items-center justify-between text-xs text-foreground-500 mb-0.5">
                    <span className="truncate">{tp.title}</span>
                    <span>{tp.pct}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-background-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${tp.pct === 100 ? 'bg-emerald-500' : tp.pct > 0 ? 'bg-amber-400' : 'bg-background-200'}`} style={{ width: `${tp.pct}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* Main Area */}
          <div className="flex-1 min-w-0">
            {/* View: Recorrido paso a paso */}
            {view === 'recorrido' && (
              <div>
                {/* Topic progress header */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2">
                      <i className={`${currentTopicCfg?.icon ?? 'ri-question-line'} ${currentTopicCfg?.color ?? 'text-foreground-500'}`}></i>
                      {currentTopicCfg?.title ?? 'Recorrido'}
                    </h2>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePrevTopic}
                        disabled={activeTopic === welcomeTopicConfigs[0].key}
                        className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <i className="ri-arrow-left-s-line"></i>
                      </button>
                      <span className="text-xs text-foreground-500 font-medium">
                        Tema {welcomeTopicConfigs.findIndex((t) => t.key === activeTopic) + 1} de {welcomeTopicConfigs.length}
                      </span>
                      <button
                        onClick={handleNextTopic}
                        disabled={isLastTopic}
                        className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* Topic description */}
                  <div className={`p-4 rounded-xl bg-gradient-to-r ${currentTopicCfg?.gradient ?? 'from-emerald-400 to-teal-500'} text-white mb-4`}>
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
                        <i className={`${currentTopicCfg?.icon ?? 'ri-question-line'} text-2xl`}></i>
                      </div>
                      <div>
                        <div className="font-bold text-lg">{currentTopicCfg?.subtitle}</div>
                        <div className="text-sm text-white/80">{currentTopicCfg?.description}</div>
                      </div>
                    </div>
                  </div>

                  {/* Step dots */}
                  <div className="flex items-center justify-center gap-1.5 mb-6">
                    {topicSteps.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentStepIndex(i)}
                        className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                          i === currentStepIndex
                            ? `w-6 ${currentTopicCfg?.color === 'text-emerald-600' ? 'bg-emerald-500' : currentTopicCfg?.color === 'text-amber-600' ? 'bg-amber-500' : currentTopicCfg?.color === 'text-rose-600' ? 'bg-rose-500' : currentTopicCfg?.color === 'text-violet-600' ? 'bg-violet-500' : 'bg-sky-500'}`
                            : completedSteps.has(topicSteps[i]?.id ?? '')
                              ? 'bg-emerald-400'
                              : 'bg-background-200'
                        }`}
                      ></button>
                    ))}
                  </div>
                </div>

                {/* Current Step Card */}
                {currentStep && (
                  <div className={`bg-background-50 rounded-xl border ${currentTopicCfg?.borderColor ?? 'border-background-200/70'} overflow-hidden`}>
                    {/* Visual */}
                    <div className="relative w-full h-64 md:h-80 overflow-hidden">
                      <img src={currentStep.visualUrl} alt={currentStep.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                      {/* Step number */}
                      <div className="absolute top-4 left-4 flex items-center gap-2">
                        <span className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white text-sm font-bold">
                          {currentStepIndex + 1}
                        </span>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          currentStep.format === 'video' ? 'bg-rose-500/80 text-white' :
                          currentStep.format === 'guia' ? 'bg-sky-500/80 text-white' :
                          'bg-amber-500/80 text-white'
                        }`}>
                          {currentStep.format === 'video' ? 'Video' : currentStep.format === 'guia' ? 'Guía Visual' : 'Paso Rápido'}
                        </span>
                      </div>

                      {/* Duration */}
                      <div className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-black/50 text-white text-xs font-medium">
                        {currentStep.durationSeconds}s
                      </div>

                      {/* Title overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2">{currentStep.title}</h3>
                        <p className="text-white/80 text-sm md:text-base max-w-2xl">{currentStep.description}</p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleCompleteStep(currentStep.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                            completedSteps.has(currentStep.id)
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-emerald-500 text-white hover:bg-emerald-600'
                          }`}
                        >
                          <i className={`${completedSteps.has(currentStep.id) ? 'ri-check-fill' : 'ri-check-line'}`}></i>
                          {completedSteps.has(currentStep.id) ? 'Completado' : 'Marcar como visto'}
                        </button>
                        <a
                          href={currentStep.ctaLink}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap bg-background-100 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                        >
                          {currentStep.ctaLabel}
                          <i className="ri-arrow-right-line text-xs"></i>
                        </a>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                          disabled={currentStepIndex === 0}
                          className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <i className="ri-arrow-left-s-line text-lg"></i>
                        </button>
                        <span className="text-xs text-foreground-500 font-medium min-w-[60px] text-center">
                          {currentStepIndex + 1} / {topicSteps.length}
                        </span>
                        <button
                          onClick={() => {
                            if (isLastStepInTopic) {
                              handleNextTopic();
                            } else {
                              setCurrentStepIndex(currentStepIndex + 1);
                            }
                          }}
                          className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
                        >
                          <i className={`ri-arrow-right-s-line text-lg ${isLastStepOverall ? 'opacity-30' : ''}`}></i>
                        </button>
                      </div>
                    </div>

                    {/* Completion celebration */}
                    {isLastStepOverall && overallProgress.pct === 100 && (
                      <div className="mx-4 mb-4 p-5 bg-gradient-to-r from-emerald-50 via-amber-50 to-rose-50 border border-emerald-200 rounded-xl text-center">
                        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                          <i className="ri-sparkling-fill text-3xl text-emerald-600"></i>
                        </div>
                        <div className="text-lg font-bold text-emerald-700 mb-1">¡Recorrido completo!</div>
                        <div className="text-sm text-emerald-600 mb-4">Ya sabés todo lo necesario para disfrutar RFW. ¡A explorar!</div>
                        <a
                          href="/explorar"
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-full text-sm font-semibold hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-compass-line"></i>
                          Empezar a explorar
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* All steps list below */}
                <div className="mt-6">
                  <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">
                    Todos los pasos de este tema
                  </div>
                  <div className="space-y-2">
                    {topicSteps.map((step, i) => {
                      const isCurrent = i === currentStepIndex;
                      const isDone = completedSteps.has(step.id);
                      const formatIcon = step.format === 'video' ? 'ri-play-circle-fill' : step.format === 'guia' ? 'ri-book-open-fill' : 'ri-footprint-fill';
                      return (
                        <button
                          key={step.id}
                          onClick={() => setCurrentStepIndex(i)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors cursor-pointer ${
                            isCurrent
                              ? `${currentTopicCfg?.bgColor ?? 'bg-emerald-50'} border ${currentTopicCfg?.borderColor ?? 'border-emerald-200'}`
                              : isDone
                                ? 'bg-emerald-50/50 border border-emerald-100'
                                : 'bg-background-50 border border-background-200/70 hover:bg-background-100'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isDone ? 'bg-emerald-500 text-white' : isCurrent ? `${currentTopicCfg?.bgColor ?? 'bg-emerald-50'} ${currentTopicCfg?.color ?? 'text-emerald-600'}` : 'bg-background-100 text-foreground-400'
                          }`}>
                            {isDone ? <i className="ri-check-fill"></i> : <span className="text-xs font-bold">{i + 1}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <i className={`${step.icon} text-xs ${isDone ? 'text-emerald-600' : isCurrent ? (currentTopicCfg?.color ?? 'text-foreground-600') : 'text-foreground-400'}`}></i>
                              <span className={`text-sm font-medium truncate ${isDone ? 'text-emerald-700' : 'text-foreground-950'}`}>{step.title}</span>
                            </div>
                            <div className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{step.description}</div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full ${
                              step.format === 'video' ? 'bg-rose-50 text-rose-600' : step.format === 'guia' ? 'bg-sky-50 text-sky-600' : 'bg-amber-50 text-amber-600'
                            }`}>
                              <i className={`${formatIcon} text-xs`}></i>
                              {step.durationSeconds}s
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* View: Temas */}
            {view === 'temas' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-stack-line text-amber-600"></i>
                  Los 5 Temas de Bienvenida
                </h2>
                <div className="space-y-4">
                  {welcomeTopicConfigs.map((cfg, i) => {
                    const steps = getStepsByTopic(cfg.key);
                    const duration = steps.reduce((sum, s) => sum + s.durationSeconds, 0);
                    const progress = topicProgress.find((p) => p.key === cfg.key);
                    return (
                      <div key={cfg.key} className={`bg-background-50 rounded-lg border ${cfg.borderColor} overflow-hidden`}>
                        <div className={`p-4 bg-gradient-to-r ${cfg.gradient} text-white`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-bold text-white/70">#{i + 1}</span>
                              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                                <i className={`${cfg.icon} text-2xl`}></i>
                              </div>
                              <div>
                                <div className="font-bold text-lg">{cfg.title}</div>
                                <div className="text-sm text-white/80">{cfg.subtitle}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-white/70">{steps.length} pasos · {duration}s</div>
                              {progress && (
                                <div className="w-20 h-1.5 bg-white/20 rounded-full overflow-hidden mt-1">
                                  <div className="h-full bg-white rounded-full" style={{ width: `${progress.pct}%` }}></div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <p className="text-sm text-foreground-600 mb-4">{cfg.description}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                            {steps.map((step) => (
                              <div key={step.id} className="flex items-center gap-2 p-2.5 bg-background-100 rounded-lg">
                                <i className={`${step.icon} ${cfg.color} text-sm`}></i>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-foreground-950 truncate">{step.title}</div>
                                  <div className="text-xs text-foreground-400">{step.durationSeconds}s</div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-4">
                            <button
                              onClick={() => { setActiveTopic(cfg.key); setView('recorrido'); setCurrentStepIndex(0); }}
                              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${cfg.bgColor} ${cfg.color} border ${cfg.borderColor} hover:opacity-80`}
                            >
                              <i className="ri-play-line"></i>
                              Iniciar este tema
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Videos */}
            {view === 'videos' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-play-circle-line text-rose-600"></i>
                  Videos Cortos
                  <span className="text-sm font-normal text-foreground-500">({welcomeVideos.length} videos)</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {welcomeVideos.map((video) => {
                    const tCfg = welcomeTopicConfigs.find((t) => t.key === video.topic);
                    const durationDisplay = `${Math.floor(video.durationSeconds / 60)}:${String(video.durationSeconds % 60).padStart(2, '0')}`;
                    return (
                      <div key={video.id} className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-rose-200 transition-colors group cursor-pointer">
                        <div className="relative w-full h-48 overflow-hidden">
                          <img src={video.thumbnailUrl} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors"></div>
                          {/* Play button */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all">
                              <i className="ri-play-fill text-2xl text-rose-600 ml-0.5"></i>
                            </div>
                          </div>
                          {/* Duration */}
                          <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/60 text-white text-xs font-medium">
                            {durationDisplay}
                          </div>
                          {/* Topic badge */}
                          <div className="absolute top-3 left-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tCfg?.bgColor ?? 'bg-background-100'} ${tCfg?.color ?? 'text-foreground-600'}`}>
                              {tCfg?.title}
                            </span>
                          </div>
                        </div>
                        <div className="p-3">
                          <div className="font-semibold text-sm text-foreground-950 mb-1">{video.title}</div>
                          <p className="text-xs text-foreground-600">{video.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Guías */}
            {view === 'guias' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-book-open-line text-sky-600"></i>
                  Guías Visuales
                  <span className="text-sm font-normal text-foreground-500">({welcomeGuides.length} guías)</span>
                </h2>
                <div className="space-y-4">
                  {welcomeGuides.map((guide) => {
                    const tCfg = welcomeTopicConfigs.find((t) => t.key === guide.topic);
                    return (
                      <div key={guide.id} className={`bg-background-50 rounded-lg border ${tCfg?.borderColor ?? 'border-background-200/70'} overflow-hidden`}>
                        <div className="flex flex-col md:flex-row">
                          <div className="w-full md:w-80 h-56 md:h-auto flex-shrink-0 overflow-hidden">
                            <img src={guide.visualUrl} alt={guide.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 p-5">
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-10 h-10 rounded-xl ${guide.bgColor} flex items-center justify-center`}>
                                <i className={`${guide.icon} text-lg ${guide.color}`}></i>
                              </div>
                              <div>
                                <div className="font-bold text-base text-foreground-950">{guide.title}</div>
                                <span className={`text-xs font-medium ${tCfg?.color ?? 'text-foreground-500'}`}>{tCfg?.title}</span>
                              </div>
                            </div>
                            <p className="text-sm text-foreground-600 leading-relaxed">{guide.content}</p>
                            <button
                              onClick={() => { setActiveTopic(guide.topic); setView('recorrido'); }}
                              className={`mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${guide.bgColor} ${guide.color} border ${tCfg?.borderColor ?? 'border-background-200'} hover:opacity-80`}
                            >
                              <i className="ri-play-line"></i>
                              Ver recorrido de este tema
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* View: Reglas */}
            {view === 'reglas' && (
              <div>
                <h2 className="text-lg font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <i className="ri-scales-line text-violet-600"></i>
                  Reglas del Sistema de Bienvenida
                </h2>
                <div className="space-y-4 mb-6">
                  {welcomeRules.map((rule) => (
                    <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl ${rule.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                        </div>
                        <div>
                          <div className="font-semibold text-base text-foreground-950 mb-2">{rule.title}</div>
                          <div className="text-sm text-foreground-600 leading-relaxed">{rule.description}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Onboarding stats box */}
                <div className="bg-emerald-50 rounded-lg p-5 border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-bar-chart-line text-lg text-emerald-600"></i>
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-emerald-800 mb-2">Resultados del Sistema de Bienvenida</div>
                      <div className="text-sm text-emerald-700 leading-relaxed">
                        Hoy se unieron <strong>{welcomeStats.totalNewUsersToday.toLocaleString()}</strong> nuevos oyentes a RFW. De ellos, <strong>{welcomeStats.totalCompletedOnboarding.toLocaleString()}</strong> completaron el recorrido de bienvenida ({welcomeStats.completionRate}% de tasa de finalización). El tiempo promedio de finalización es de <strong>{welcomeStats.averageCompletionSeconds} segundos</strong> — muy por debajo del límite de 2 minutos. El tema más visto es <strong>"Cómo Ganar Puntos"</strong> y el más saltado es <strong>"Cómo Entrar a Clubes"</strong>, lo que sugiere que los nuevos usuarios priorizan entender el sistema de recompensas antes que la afiliación a comunidades.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Format distribution */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { format: 'paso', label: 'Pasos Rápidos', icon: 'ri-footprint-line', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', count: welcomeSteps.filter((s) => s.format === 'paso').length, desc: 'Acciones concretas e inmediatas' },
                    { format: 'video', label: 'Videos Cortos', icon: 'ri-play-circle-line', color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-200', count: welcomeSteps.filter((s) => s.format === 'video').length, desc: 'Demostraciones visuales rápidas' },
                    { format: 'guia', label: 'Guías Visuales', icon: 'ri-book-open-line', color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200', count: welcomeSteps.filter((s) => s.format === 'guia').length, desc: 'Infografías y referencias' },
                  ].map((f) => (
                    <div key={f.format} className={`bg-background-50 rounded-lg p-4 border ${f.border} text-center`}>
                      <div className={`w-12 h-12 mx-auto rounded-xl ${f.bg} flex items-center justify-center mb-3`}>
                        <i className={`${f.icon} text-xl ${f.color}`}></i>
                      </div>
                      <div className={`font-semibold text-sm ${f.color} mb-1`}>{f.label}</div>
                      <div className="text-2xl font-bold text-foreground-950 mb-1">{f.count}</div>
                      <div className="text-xs text-foreground-500">{f.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cross Links */}
        <div className="mt-10 pt-8 border-t border-background-200/70">
          <div className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-3">También te puede interesar</div>
          <div className="flex flex-wrap gap-2">
            {[
              { href: '/gamificacion', label: 'Gamificación', icon: 'ri-gamepad-line' },
              { href: '/niveles', label: 'Niveles', icon: 'ri-medal-line' },
              { href: '/rachas', label: 'Rachas', icon: 'ri-fire-line' },
              { href: '/rankings', label: 'Rankings', icon: 'ri-trophy-line' },
              { href: '/clubes', label: 'Clubes', icon: 'ri-team-line' },
              { href: '/explorar', label: 'Explorar', icon: 'ri-compass-line' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-100 rounded-full text-xs text-foreground-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={link.icon}></i>
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
            <i className="ri-timer-line text-emerald-600"></i>
            <span className="text-sm font-semibold text-emerald-700">Menos de 2 minutos.</span>
          </div>
        </div>
      </div>
    </div>
  );
}